import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContactsForDashboard } from '../store/slices/contactSlice';
import { fetchTemplates } from '../store/slices/templateSlice';
import { fetchCampaigns } from '../store/slices/campaignSlice';
import { UsersIcon, DocumentTextIcon, EnvelopeIcon, PaperAirplaneIcon, AcademicCapIcon, HeartIcon, BuildingLibraryIcon, BuildingOfficeIcon, BriefcaseIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { INSTITUTION_TYPES } from '../utils/constants';
import UserStatsCard from '../components/UserStatsCard';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { total: contactCount, institutionTypeBreakdown } = useSelector((state) => state.contacts);
  const { items: templates } = useSelector((state) => state.templates);
  const { items: campaigns } = useSelector((state) => state.campaigns);
  const { user } = useSelector((state) => state.auth);

  // Only fetch templates and campaigns for superadmin
  useEffect(() => {
    // Always fetch dashboard-specific data
    dispatch(fetchContactsForDashboard());

    // Only fetch templates and campaigns for superadmin
    if (user?.role === 'superadmin') {
      dispatch(fetchTemplates()).catch(error => {
        console.log('Failed to fetch templates:', error);
      });
      dispatch(fetchCampaigns()).catch(error => {
        console.log('Failed to fetch campaigns:', error);
      });
    }
  }, [dispatch, user?.role]);

  // Get user's accessible institution types - superadmin gets all by default
  const userInstitutionTypes = user?.role === 'superadmin'
    ? INSTITUTION_TYPES
    : (user?.institutionTypes || []);

  // Calculate institution type statistics using breakdown from API
  const institutionStats = userInstitutionTypes.map(institutionType => {
    return {
      name: institutionType,
      count: institutionTypeBreakdown[institutionType] || 0,
      icon: getInstitutionIcon(institutionType)
    };
  });

  function getInstitutionIcon(institutionType) {
    const icons = {
      'Schools': BuildingLibraryIcon,
      'College': AcademicCapIcon,
      'Universities': AcademicCapIcon,
      'Government Departments': BuildingOfficeIcon,
      'Ministries': BuildingOfficeIcon,
      'Embassies & High Commissions': BuildingOfficeIcon,
      'NGOs & Foundations': HeartIcon,
      'Education Boards': DocumentTextIcon,
      'Corporate Partners': BuildingOffice2Icon,
      'Individual Officials (Ambassadors, Education Officers, Directors)': BriefcaseIcon,
      'Other': UsersIcon
    };
    return icons[institutionType] || UsersIcon;
  }

  // Stats configuration - only show template/campaign stats for superadmin
  const baseStats = [
    {
      name: 'Total Contacts',
      value: contactCount, // Use total count from state (optimized)
      icon: UsersIcon,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50'
    }
  ];

  const superadminStats = [
    {
      name: 'Templates',
      value: templates.length,
      icon: DocumentTextIcon,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50'
    },
    {
      name: 'Campaigns',
      value: campaigns.length,
      icon: EnvelopeIcon,
      color: 'bg-green-500',
      bgColor: 'bg-green-50'
    },
    {
      name: 'Sent Emails',
      value: campaigns.reduce((sum, c) => sum + (c.sentCount || 0), 0),
      icon: PaperAirplaneIcon,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-50'
    }
  ];

  const stats = user?.role === 'superadmin'
    ? [...baseStats, ...superadminStats]
    : baseStats;

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">Dashboard</h1>
        <p className="mt-2 text-base text-gray-600 font-medium">Welcome back, <span className="text-blue-600 font-semibold">{user?.name}</span>! Manage your contacts and communications.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white overflow-hidden rounded-xl border border-gray-200 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer">
            <div className="p-5 sm:p-6">
              <div className="flex flex-col items-center text-center">
                <div className={`bg-gradient-to-br ${stat.color} ${stat.color.replace('500', '600')} rounded-xl p-4 mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <stat.icon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                </div>
                <div className="w-full">
                  <dd className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{stat.value}</dd>
                  <dt className="text-sm sm:text-base font-semibold text-gray-600">{stat.name}</dt>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Institution Type Statistics */}
      {userInstitutionTypes.length > 0 && (
        <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-2xl p-6 sm:p-8 border border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-6">Your Institution Types</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {institutionStats.map((institution) => (
              <a
                key={institution.name}
                href={`/institution/${institution.name ? institution.name.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'unknown'}`}
                className="flex flex-col items-center p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 hover:scale-105 group cursor-pointer"
              >
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-4 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-md">
                  <institution.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 text-center text-sm mb-1">{institution.name}</h3>
                <p className="text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{institution.count} contacts</p>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* User Activity Statistics - Superadmin Only */}
      {user?.role === 'superadmin' && (
        <UserStatsCard />
      )}

      <div className="bg-gradient-to-br from-white to-gray-50 shadow-xl rounded-2xl p-6 sm:p-8 border border-gray-200">
        <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
          {user?.role === 'superadmin' && (
            <>
              <a
                href="/templates"
                className="flex flex-col items-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:shadow-xl transition-all duration-300 hover:scale-105 group cursor-pointer"
              >
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-gray-900 text-lg mb-1">Create Template</h3>
                  <p className="text-sm text-gray-600 font-medium">Design email templates</p>
                </div>
              </a>
              <a
                href="/campaigns"
                className="flex flex-col items-center p-6 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl border-2 border-green-200 hover:border-green-400 hover:shadow-xl transition-all duration-300 hover:scale-105 group cursor-pointer"
              >
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <EnvelopeIcon className="h-8 w-8 text-white" />
                </div>
                <div className="text-center">
                  <h3 className="font-bold text-gray-900 text-lg mb-1">Send Campaign</h3>
                  <p className="text-sm text-gray-600 font-medium">Launch email campaigns</p>
                </div>
              </a>
            </>
          )}
          {user?.role === 'superadmin' && (
            <a
              href="/users"
              className="flex flex-col items-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-xl transition-all duration-300 hover:scale-105 group cursor-pointer"
            >
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <UsersIcon className="h-8 w-8 text-white" />
              </div>
              <div className="text-center">
                <h3 className="font-bold text-gray-900 text-lg mb-1">Manage Users</h3>
                <p className="text-sm text-gray-600 font-medium">Add and manage users</p>
              </div>
            </a>
          )}

          {/* Show contact-related actions for all users */}
          <a
            href="/contacts"
            className="flex flex-col items-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 hover:scale-105 group cursor-pointer"
          >
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
              <UsersIcon className="h-8 w-8 text-white" />
            </div>
            <div className="text-center">
              <h3 className="font-bold text-gray-900 text-lg mb-1">Manage Contacts</h3>
              <p className="text-sm text-gray-600 font-medium">Add and organize contacts</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
